<script setup lang="ts">
/**
 * The two steps for a direct payment account waiting for the bank: download and send the agreement, then the bank's
 * confirmation. Used by DirectPaymentAccountOnboardingModal and by the route /accounts/:id/direct-payment.
 */
import { ArrowLeft, ArrowRight } from 'lucide-vue-next'
import { Button } from 'pejer/src/components/ui/button'
import { ref } from 'vue'
import DirectPaymentAccountOnboardingForm from './DirectPaymentAccountOnboardingForm.vue'
import DirectPaymentAccountVerificationStep from './DirectPaymentAccountVerificationStep.vue'

const props = defineProps<{
  businessName: string
  organizationId: string
  countryCode: string
  bban: string

  contactPostalCode: string
  contactPersonName: string
  contactPersonEmail: string
}>()

const emits = defineEmits<{
  (e: 'accountCreated'): void
  (e: 'update:finished', value: boolean): void
  (e: 'close'): void
}>()

const step = ref<1 | 2>(1)

/** Back to the first step, e.g. when the dialog opens again. */
function reset() {
  step.value = 1
}
defineExpose({ reset })
</script>
<template>
  <template v-if="step === 1">
    <DirectPaymentAccountOnboardingForm
      :businessName="props.businessName"
      :organizationId="props.organizationId"
      :countryCode="props.countryCode"
      :bban="props.bban"
      :contactPostalCode="props.contactPostalCode"
      :contactPersonName="props.contactPersonName"
      :contactPersonEmail="props.contactPersonEmail"
      @accountCreated="emits('accountCreated')"
      @update:finished="emits('update:finished', $event)"
    />
    <div class="flex justify-end border-t pt-4 mt-2">
      <Button variant="outline" class="gap-2" @click="step = 2">
        Jeg har allerede sendt avtalen til banken
        <ArrowRight :size="16" />
      </Button>
    </div>
  </template>
  <template v-else>
    <DirectPaymentAccountVerificationStep :businessName="props.businessName" :bban="props.bban" />
    <div class="flex justify-between border-t pt-4 mt-2">
      <Button variant="ghost" class="gap-2" @click="step = 1">
        <ArrowLeft :size="16" /> Tilbake
      </Button>
      <Button @click="emits('close')">Lukk</Button>
    </div>
  </template>
</template>
